/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawer-sections/CollapsibleSection.tsx
 * FILE_ID: UI-KANBAN-DRAWER-COLLAPSIBLE-SECTION-001
 * PURPOSE: The shared collapsed-by-default section shell for the Card tab —
 *   a header row (title + count badge + chevron) that reveals its children
 *   on expand. Collapsed children are UNMOUNTED (progressive disclosure =
 *   don't pay render/fetch cost for content nobody asked to see).
 * WHY: ARC-4 U02 drawer IA — "crowded and unviewable". The squint-test goal:
 *   action date + next action visible without scrolling; everything else a
 *   quiet labelled row with a count.
 * FLOW:
 *   ┌───────────────┐   ┌────────────────┐   ┌───────────────────────┐
 *   │ expanded flag │ → │ header button  │ → │ children (mounted     │
 *   │ + onToggle    │   │ (count, chevr) │   │ only while expanded)  │
 *   └───────────────┘   └────────────────┘   └───────────────────────┘
 * RELATED:
 *   - ./drawerSectionPrefs.ts — expand persistence + classic revert flag.
 *   - ./CardTabContent.tsx — sole host; ARC-1/2/3 mount INTO these slots.
 */
import type { ReactNode } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'

import type { DrawerSectionId } from './drawerSectionPrefs'

export interface CollapsibleSectionProps {
  id: DrawerSectionId
  title: string
  /** Small count badge next to the title (e.g. activity entries, members).
   *  Pass a string for compound counts like "3/7" checklist progress.
   *  Omit when no cheap count exists — the header renders without a badge. */
  count?: number | string
  icon?: ReactNode
  expanded: boolean
  onToggle: (id: DrawerSectionId) => void
  children: ReactNode
}

export function CollapsibleSection({
  id,
  title,
  count,
  icon,
  expanded,
  onToggle,
  children,
}: CollapsibleSectionProps) {
  const regionId = `card-drawer-section-${id}`
  return (
    <section
      className="rounded-xl border border-neutral-200/70 dark:border-neutral-800"
      data-testid={`drawer-section-${id}`}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={regionId}
        onClick={() => onToggle(id)}
        className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left hover:bg-neutral-50 dark:hover:bg-neutral-900/60"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
        )}
        {icon && <span className="shrink-0 text-neutral-400 dark:text-neutral-500">{icon}</span>}
        <span className="min-w-0 flex-1 truncate text-[12px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
          {title}
        </span>
        {count !== undefined && count !== 0 && count !== '' && (
          <span className="shrink-0 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10.5px] font-medium tabular-nums text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
            {count}
          </span>
        )}
      </button>
      {expanded && (
        <div id={regionId} className="px-3 pb-3 pt-1">
          {children}
        </div>
      )}
    </section>
  )
}
